import { useState, useEffect, useCallback, useRef, useMemo } from "react";

const useScrollbarDetection = (selector = ".ps-sidebar-root .ps-sidebar-container", dependencies = []) => {
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const observersRef = useRef({ resize: null, mutation: null });
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // Stringify dependencies to avoid issues with array spreading
  // Handle cases where dependencies might contain non-serializable values
  const depsKey = useMemo(() => {
    try {
      return JSON.stringify(dependencies);
    } catch (error) {
      console.warn("Failed to stringify dependencies, using length as fallback:", error);
      return dependencies.length;
    }
  }, [dependencies]);

  // Memoize checkScrollbar to prevent unnecessary re-creation
  const checkScrollbar = useCallback(() => {
    try {
      const element = document.querySelector(selector);

      if (!element) {
        if (isMountedRef.current) {
          setHasScrollbar(false);
        }
        return;
      }

      // Check if scrollbar is actually visible by comparing offsetWidth and clientWidth
      // If scrollbar is visible, offsetWidth > clientWidth
      const hasVerticalScrollbar = element.offsetWidth > element.clientWidth;

      if (isMountedRef.current) {
        setHasScrollbar(hasVerticalScrollbar);
      }
    } catch (error) {
      console.error("Error in checkScrollbar:", error);
      if (isMountedRef.current) {
        setHasScrollbar(false);
      }
    }
  }, [selector]);

  const cleanupResources = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (observersRef.current.resize) {
      observersRef.current.resize.disconnect();
      observersRef.current.resize = null;
    }

    if (observersRef.current.mutation) {
      observersRef.current.mutation.disconnect();
      observersRef.current.mutation = null;
    }
  }, []);

  const setupResizeObserver = useCallback(
    (element) => {
      if (!element) return;

      const canUseResizeObserver = typeof ResizeObserver !== "undefined";
      if (!canUseResizeObserver) return;

      try {
        observersRef.current.resize = new ResizeObserver(checkScrollbar);
        observersRef.current.resize.observe(element);
      } catch (error) {
        console.error("Error setting up ResizeObserver:", error);
        observersRef.current.resize = null;
      }
    },
    [checkScrollbar]
  );

  // Observer for case change setting show/hide scrollbar
  const setupMutationObserver = useCallback(
    (element) => {
      if (!element) return;

      const canUseMutationObserver = typeof MutationObserver !== "undefined";
      if (!canUseMutationObserver) return;

      try {
        observersRef.current.mutation = new MutationObserver(checkScrollbar);
        observersRef.current.mutation.observe(element, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      } catch (error) {
        console.error("Error setting up MutationObserver:", error);
        observersRef.current.mutation = null;
      }
    },
    [checkScrollbar]
  );

  useEffect(() => {
    isMountedRef.current = true;

    // Cleanup any existing resources before setup
    cleanupResources();

    // Initial check with error handling
    try {
      checkScrollbar();
    } catch (error) {
      console.error("Error during initial scrollbar check:", error);
    }

    // Setup window resize listener with passive option for better performance
    window.addEventListener("resize", checkScrollbar, { passive: true });

    // Delayed check to ensure DOM is updated
    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        checkScrollbar();
      }
    }, 100);

    // Get element and setup observers
    try {
      const element = document.querySelector(selector);

      if (element) {
        setupResizeObserver(element);
        setupMutationObserver(element);
      }
    } catch (error) {
      console.error("Error setting up observers:", error);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;

      try {
        window.removeEventListener("resize", checkScrollbar);
      } catch (error) {
        console.error("Error removing resize listener:", error);
      }

      cleanupResources();
    };
  }, [selector, checkScrollbar, cleanupResources, setupResizeObserver, setupMutationObserver, depsKey]);

  return hasScrollbar;
};

export default useScrollbarDetection;
